import { useEffect, useState } from 'react';
import { RefreshCw, CheckCircle, XCircle, Clock } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { AdminPageHeader } from '../../components/admin/AdminPageHeader';

interface FunctionHealth {
  name: string; status: 'ok' | 'error' | 'slow' | 'unknown';
  lastRun: string | null; avgMs: number | null; errorRate: number;
}

const FRIENDLY_NAMES: Record<string, string> = {
  'forecast': 'Previsão Astrológica',
  'forecast-stream': 'Previsão (Streaming)',
  'birth-chart': 'Mapa Astral (Cálculo)',
  'birth-chart-interp': 'Mapa Astral (Interpretação)',
  'synastry': 'Sinastria',
  'synastry-stream': 'Sinastria (Streaming)',
  'questions': 'Perguntar aos Astros',
  'tarot-reading': 'Tarot',
  'numerology': 'Numerologia',
  'i-ching': 'I Ching',
  'dream-journal': 'Diário de Sonhos',
  'astromatch-analyze': 'AstroMatch',
  'astro-pet': 'Astro Pet',
  'atlas-astral-custom': 'Atlas Astral (Cidade)',
  'atlas-astral-global': 'Atlas Astral (Global)',
  'human-design': 'Design Humano',
  'vedic-astrology': 'Astrologia Védica',
  'quiromancy': 'Quiromancia',
  'start-astral': 'Start Astral',
  'astro-coach-module': 'Astro Coach',
  'create-checkout-session': 'Checkout (Stripe)',
  'stripe-webhook': 'Webhook Stripe',
  'customer-portal': 'Portal do Cliente',
  'send-email': 'Envio de E-mail',
  'send-push-notification': 'Notificação Push',
  'business-metrics': 'Métricas de Negócio',
  'admin-get-user': 'Admin: Buscar Usuário',
  'admin-grant-credits': 'Admin: Conceder Créditos',
  'admin-issue-refund': 'Admin: Reembolso',
};

// Edge functions that power the platform
const EDGE_FUNCTIONS = [
  'forecast', 'birth-chart', 'synastry', 'questions', 'tarot-reading',
  'numerology', 'i-ching', 'dream-journal', 'astromatch-analyze', 'astro-pet',
  'atlas-astral-custom', 'atlas-astral-global', 'astrocartografia', 'human-design', 'vedic-astrology',
  'quiromancy', 'start-astral', 'astro-coach-module',
  'partner-analysis', 'retrograde-analysis', 'eclipse-analysis',
  'create-checkout-session', 'stripe-webhook', 'customer-portal',
  'send-email', 'send-push-notification', 'business-metrics',
  'admin-get-user', 'admin-grant-credits', 'admin-issue-refund',
];

export function AdminFunctionsPage() {
  const [functions, setFunctions] = useState<FunctionHealth[]>([]);
  const [loading,   setLoading]   = useState(false);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [healthLog, setHealthLog] = useState<{ fn: string; status: string; ms: number; created_at: string }[]>([]);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);

    // Load health logs from the last 24h
    const since = new Date(Date.now() - 24 * 3600000).toISOString();
    const { data: logs } = await supabase
      .from('edge_function_health_log')
      .select('fn, status, ms, created_at')
      .gte('created_at', since)
      .order('created_at', { ascending: false })
      .limit(500);

    const rows = logs ?? [];
    setHealthLog(rows.slice(0, 20));

    // Build per-function health
    const byFn: Record<string, { runs: { status: string; ms: number }[] }> = {};
    rows.forEach(r => {
      if (!byFn[r.fn]) byFn[r.fn] = { runs: [] };
      byFn[r.fn].runs.push({ status: r.status, ms: r.ms });
    });

    const health: FunctionHealth[] = EDGE_FUNCTIONS.map(name => {
      const fnData = byFn[name];
      if (!fnData || fnData.runs.length === 0) {
        return { name, status: 'unknown', lastRun: null, avgMs: null, errorRate: 0 };
      }
      const runs = fnData.runs;
      const errors = runs.filter(r => r.status === 'error').length;
      const errorRate = (errors / runs.length) * 100;
      const avgMs = runs.reduce((s, r) => s + (r.ms ?? 0), 0) / runs.length;
      let status: FunctionHealth['status'] = 'ok';
      if (errorRate > 10) status = 'error';
      else if (avgMs > 5000) status = 'slow';
      return { name, status, lastRun: rows.find(r => r.fn === name)?.created_at ?? null, avgMs: Math.round(avgMs), errorRate: Math.round(errorRate) };
    });

    setFunctions(health);
    setLastChecked(new Date());
    setLoading(false);
  }

  const statusIcon = (s: FunctionHealth['status']) => {
    if (s === 'ok')      return <CheckCircle size={14} color="#4ade80" />;
    if (s === 'error')   return <XCircle size={14} color="#f87171" />;
    if (s === 'slow')    return <Clock size={14} color="#fbbf24" />;
    return <Clock size={14} color="var(--text-muted)" />;
  };

  const counts = { ok: 0, error: 0, slow: 0, unknown: 0 };
  functions.forEach(f => { counts[f.status]++; });

  return (
    <div>
      <AdminPageHeader title="Edge Functions (Funções do Sistema)" objective="Monitora as funções de backend que executam os serviços do Professya. Latência = tempo de resposta em milissegundos (ms). Taxa de erro = % das chamadas que falharam. Saudável = latência < 5s e erros < 5%." />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h1 className="font-display" style={{ fontSize: '1.5rem', color: 'var(--text-primary)', margin: 0 }}>Edge Functions</h1>
          {lastChecked && (
            <p style={{ margin: '0.25rem 0 0', fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
              Última verificação: {lastChecked.toLocaleTimeString('pt-BR')}
            </p>
          )}
        </div>
        <button className="btn btn-secondary btn-sm" onClick={load} disabled={loading}
          style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Atualizar
        </button>
      </div>

      {/* Summary pills */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
        {[
          { label: 'Saudáveis', count: counts.ok,      color: '#4ade80', bg: 'rgba(74,222,128,0.1)' },
          { label: 'Lentas',    count: counts.slow,    color: '#fbbf24', bg: 'rgba(251,191,36,0.1)' },
          { label: 'Com erro',  count: counts.error,   color: '#f87171', bg: 'rgba(239,68,68,0.1)' },
          { label: 'Sem dados', count: counts.unknown, color: 'var(--text-muted)', bg: 'rgba(255,255,255,0.04)' },
        ].map(s => (
          <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0.875rem', borderRadius: 'var(--radius-full)', backgroundColor: s.bg, border: `1px solid ${s.color}33` }}>
            <span style={{ fontWeight: 700, color: s.color, fontSize: '1rem', fontFamily: 'var(--font-display)' }}>{s.count}</span>
            <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>{s.label}</span>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="card shimmer" style={{ height: '300px' }} />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '0.75rem' }}>
          {functions.map(fn => (
            <div key={fn.name} className="card" style={{
              padding: '0.875rem 1rem',
              borderLeft: `3px solid ${fn.status === 'ok' ? '#4ade80' : fn.status === 'error' ? '#f87171' : fn.status === 'slow' ? '#fbbf24' : 'var(--border-subtle)'}`,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' }}>
                {statusIcon(fn.status)}
                <div style={{ overflow: 'hidden' }}>
                  <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 'var(--text-label)', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {FRIENDLY_NAMES[fn.name] ?? fn.name}
                  </span>
                  <span style={{ fontFamily: 'monospace', fontSize: '10px', color: 'var(--text-muted)', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {fn.name}
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '1rem', fontSize: '11px', color: 'var(--text-muted)' }}>
                {fn.avgMs !== null && <span>⏱ {fn.avgMs}ms avg</span>}
                {fn.errorRate > 0 && <span style={{ color: '#fca5a5' }}>✕ {fn.errorRate}% erros</span>}
                {fn.status === 'unknown' && <span>Sem dados nas últimas 24h</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Recent health log */}
      {healthLog.length > 0 && (
        <div style={{ marginTop: '2rem' }}>
          <h2 className="font-display" style={{ fontSize: '1rem', color: 'var(--text-primary)', marginBottom: '0.875rem' }}>
            Log recente
          </h2>
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <table className="data-table" style={{ width: '100%' }}>
              <thead><tr><th>Quando</th><th>Função</th><th>Status</th><th>Tempo</th></tr></thead>
              <tbody>
                {healthLog.map((row, i) => (
                  <tr key={i}>
                    <td style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {new Date(row.created_at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                    </td>
                    <td style={{ fontFamily: 'monospace', fontSize: '12px', color: 'var(--text-secondary)' }}>{row.fn}</td>
                    <td>
                      <span style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '999px',
                        backgroundColor: row.status === 'ok' ? 'rgba(74,222,128,0.12)' : 'rgba(239,68,68,0.1)',
                        color: row.status === 'ok' ? '#4ade80' : '#fca5a5' }}>
                        {row.status}
                      </span>
                    </td>
                    <td style={{ fontSize: 'var(--text-caption)', color: row.ms > 10000 ? '#fbbf24' : 'var(--text-muted)' }}>{row.ms}ms</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
